import { IconButton, useEventCallback } from '@mui/material';
import { headCellsDocumentMarriage } from 'civil-status/constants/head-cells';
import { useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { DocumentListMarriageCrumbs } from 'civil-status/constants/breadcrumb';
import {
  DocumentMarriageFilterForm,
  DocumentMarriageVM,
} from 'civil-status/view-models/document-marriage-list';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { Delete, Visibility } from '@mui/icons-material';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentMarriageList } from 'shell/models/document-marriage';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import ListHeader from 'shell/components/shared/list-header';

export default function DocumentMarriageListPage(): React.ReactElement {
  const { userProfile } = useUserProfile();

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<DocumentMarriageVM>>({
    element: null,
    open: false,
  });
  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<DocumentMarriageVM>>({
    element: null,
    open: false,
  });

  const [marriageList, getMarriageList] = useHttpFetch<
    HttpResponseList<DocumentMarriageList>,
    Array<DocumentMarriageVM>
  >(`${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
    },
    transformer: (response) => {
      return response.data?.payload?.list.map((item) => {
        const itemResult: DocumentMarriageVM = {
          registerNumberWife: item.matricolaMoglie?.toString() || '',
          surnameWife: item.cognomeMoglie || '',
          nameWife: item.nomeMoglie || '',
          registerNumberHusband: item.matricolaMarito?.toString() || '',
          surnameHusband: item.cognomeMarito || '',
          nameHusband: item.nomeMarito || '',
          year: item.anno || '',
          documentNumber: item.numeroAtto?.toString() || '',
          numReg: item.numeroRegistro || '',
          parte: item.parte || '',
          serie: item.serie1 || '',
          serie2: item.serie2 || '',
          user: item.userLock?.trim() || '',
          locked: !item.userLock?.trim()
            ? false
            : item.userLock.trim() !== userProfile.datiLogin?.key?.usernameEsteso,
        };

        return itemResult;
      });
    },
  });

  const lockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/blocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const updateFlagMarriageDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/aggiornamento-indicatore-caricamento`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const data = marriageList.data as Array<DocumentMarriageVM>;

  const formikForm = useFormik<DocumentMarriageFilterForm>({
    initialValues: {
      page: 0,
      rowsPerPage: 10,
      orders: {},
    },
    onSubmit: noop,
  });

  // HTTP Request to get list
  const navigate = useNavigate();

  const onChangePage = useCallback(
    (page) => {
      formikForm.setFieldValue('page', page || 0);
    },
    [formikForm.setFieldValue]
  );

  const onChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...formikForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      formikForm.setValues(newDataset);
    },
    [formikForm.setValues, formikForm.values]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<DocumentMarriageVM> => {
      return new Promise((resolve) => {
        (
          lockDocument({
            data: {
              key: {
                anno: cell.year,
                numeroAtto: cell.documentNumber,
                numeroRegistro: cell.numReg,
                parte: cell.parte,
                serie1: cell.serie,
              },
            },
          }) as ResponsePromise<void>
        ).then(() => {
          navigate({
            pathname: generatePath(
              '/stato-civile/matrimonio/carica/:anno/:numeroRegistro/:numeroAtto/:parte/:serie1/:serie2',
              {
                anno: cell.year,
                numeroAtto: cell.documentNumber,
                numeroRegistro: cell.numReg,
                parte: cell.parte || 'null',
                serie1: cell.serie === ' ' ? 'null' : cell.serie,
                serie2: cell.serie2 || 'null',
              }
            ),
          });
          resolve(cell);
        });
      });
    },
    [navigate, lockDocument]
  );

  const deleteMarriageDocument = useEventCallback((marriageDocument: DocumentMarriageVM) => {
    setDeleteAlertModal({ element: marriageDocument, open: true });
  });

  /**
   * The list of document act.
   */
  const list = useMemo(() => {
    return (
      data?.map((item) => ({
        ...item,
        action: (
          <>
            <PmiTooltipError
              show={item.locked}
              message={item.locked ? `Atto bloccato dall'utente ${item.user}` : ''}
            >
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onVisibilityPress(item);
                  }}
                  disabled={item.locked}
                >
                  <Visibility />
                </IconButton>
              </span>
            </PmiTooltipError>
            <span>
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  deleteMarriageDocument(item);
                }}
                color="cdmBrand"
              >
                <Delete />
              </IconButton>
            </span>
          </>
        ),
      })) || []
    );
  }, [data, onVisibilityPress, deleteMarriageDocument]);

  return (
    <>
      <Breadcrumbs items={DocumentListMarriageCrumbs} base="stato-civile" />
      <ListHeader
        addBtnAriaLabel="Aggiungi"
        addBtnLabel="Aggiungi"
        canAdd
        onAddBtnClick={() => {
          navigate('/stato-civile/matrimonio/carica/nuovo');
        }}
        title="Lista Atti Matrimonio - Caricamento"
      />
      <Table
        ariaLabel="Lista atti matrimonio"
        page={formikForm.values.page}
        rowsPerPage={formikForm.values.rowsPerPage}
        body={list}
        count={list.length}
        head={headCellsDocumentMarriage}
        onSort={noop}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        onVisibilityPress={onVisibilityPress}
        onEditPress={noop}
        onTrashPress={noop}
        orders={formikForm.values.orders}
        isNotLayoutFixed
        noSort
        hideButtons
      />
      <DeleteAlertModal
        title="Cancellazione Atto/Matrimonio"
        message="Sei sicuro di considerare l'Atto caricato ed eliminarlo dalla lista?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            updateFlagMarriageDocument({
              data: {
                anno: deleteAlertModal.element?.year || undefined,
                cognome1: deleteAlertModal.element?.surnameHusband || undefined,
                cognome2: deleteAlertModal.element?.surnameWife || undefined,
                matricola1: deleteAlertModal.element?.registerNumberHusband || undefined,
                matricola2: deleteAlertModal.element?.registerNumberWife || undefined,
                nome1: deleteAlertModal.element?.nameHusband || undefined,
                nome2: deleteAlertModal.element?.nameWife || undefined,
                numeroAtto: deleteAlertModal.element?.documentNumber || undefined,
                numeroRegistro: deleteAlertModal.element?.numReg || undefined,
                parte: deleteAlertModal.element?.parte || undefined,
                serie: deleteAlertModal.element?.serie || undefined,
              },
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Atto/Matrimonio"
        message={`La cancellazione dell'Atto Matrimonio è avvenuta con successo.`}
        onClose={() => {
          getMarriageList();
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          getMarriageList();
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </>
  );
}

DocumentMarriageListPage.displayName = 'home page component';
